import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  useGetPosOrderQuery,
  useAddPosOrderLineMutation,
  useUpdatePosOrderLineMutation,
  useUpdatePosOrderNotesMutation,
  useSubmitPosOrderMutation,
  useClosePosOrderMutation,
  useDeletePosOrderLineMutation,
  useUpdatePosOrderCustomerMutation,
  useProcessPosOrderPaymentMutation,
  useProcessPosInvoicePaymentMutation,
  useLazyGetPosOrderInvoicesQuery,
  usePosSeatHeartbeatMutation,
  usePosSeatReleaseMutation,
  useGetTaxRatesQuery,
  useGetUpsellCampaignsQuery,
  useGetPublicCatalogMenuQuery,
  useGetCatalogReferenceQuery,
} from "../../store/api";
import { api } from "../../store/api";
import DiscountForm from "../../components/pos/DiscountForm";
import UpsellSuggestionModal from "../../components/pos/UpsellSuggestionModal";
import ModifierSelector from "../../components/pos/ModifierSelector";
import ProcessPaymentModal from "../../components/pos/ProcessPaymentModal";
import CashSessionStartupModal from "../../components/pos/CashSessionStartupModal";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Textarea from "../../components/common/Textarea";
import Modal from "../../components/common/Modal";
import {
  getPosErrorMessage,
  shouldNavigateToSeatBoard,
  POS_ERROR_CODES,
  isItemStationRequiredError,
  ITEM_STATION_REQUIRED_MESSAGE,
  isOrderingBlockedError,
} from "../../utils/posErrorHandler";
import {
  normalizeMoneyAmount,
  truncateMoneyAmount,
} from "../../utils/money";
import { useOrderRealtime } from "../../hooks/usePosRealtime";
import {
  useKeyboardShortcuts,
  POS_SHORTCUTS,
} from "../../hooks/useKeyboardShortcuts";
import useCartLeaveGuard from "../../hooks/useCartLeaveGuard";
import { useSubmitCashSessionGuard } from "../../hooks/useSubmitCashSessionGuard";
import { generateUUID } from "../../utils/uuid";
import {
  buildPaymentSignature,
  nextIdempotencyState,
} from "../../utils/paymentIdempotency";
import {
  isItemEligibleForOrderType,
  normalizeOrderTypeForEligibility,
} from "../../utils/itemEligibility";
import {
  itemHasSelectableModifiers,
  resolveItemModifierGroups,
} from "../../utils/posModifiers";
import { useStationFeature } from "../../hooks/useStationFeature";
import {
  addItemToLocalDraft,
  buildDraftStateFromOrder,
  buildSubmittedOrderSnapshot,
  changeGroupedDraftLineQty,
  clearLocalDraft,
  readLocalDraft,
  removeGroupedDraftLine,
  syncLocalDraftToServer,
  writeLocalDraft,
} from "../../utils/posLocalDraft";

const PAYMENT_CONFIRMATION_POLL_INTERVAL_MS = 2000;
const PAYMENT_CONFIRMATION_POLL_TIMEOUT_MS = 45000;

const toRateFraction = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return numeric > 1 ? numeric / 100 : numeric;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isTerminalOrderStatus = (value) => {
  const normalized = String(value || "").trim().toUpperCase();
  return (
    normalized === "COMPLETED" ||
    normalized === "CANCELLED" ||
    normalized === "VOIDED" ||
    normalized === "RETURN_VOIDED"
  );
};

const isPaymentConfirmationRequiredError = (err) => {
  const title = err?.data?.title || err?.data?.code || err?.data?.errorCode;
  return (
    err?.status === 409 &&
    String(title || "").trim().toUpperCase() === "PAYMENT_CONFIRMATION_REQUIRED"
  );
};

const pollInvoiceUntilPaid = async ({
  orderId,
  invoiceId,
  fetchOrderInvoices,
  onWaiting,
}) => {
  const startedAt = Date.now();

  while (Date.now() - startedAt < PAYMENT_CONFIRMATION_POLL_TIMEOUT_MS) {
    const invoices = await fetchOrderInvoices(orderId).unwrap();
    const list = Array.isArray(invoices) ? invoices : [];
    const invoice = list.find((candidate) => candidate?.invoiceId === invoiceId) || null;
    const status = String(invoice?.status || "").toUpperCase();

    if (status === "PAID") {
      const grandTotal = Number(invoice?.grandTotal ?? 0);
      const orderLineIds = Array.isArray(invoice?.orderLineIds)
        ? invoice.orderLineIds.filter(Boolean)
        : [];
      const hasAllocations = orderLineIds.length > 0;
      const hasValue = Number.isFinite(grandTotal) && grandTotal > 0;

      if (hasValue && hasAllocations) {
        return { state: "PAID_OK", invoice };
      }
      return { state: "PAID_EMPTY", invoice };
    }

    if (onWaiting) onWaiting(invoice);
    await sleep(PAYMENT_CONFIRMATION_POLL_INTERVAL_MS);
  }

  return { state: "TIMEOUT", invoice: null };
};

const resolveReceiptInvoiceId = (order) => {
  const invoices = Array.isArray(order?.invoices) ? order.invoices : [];
  if (invoices.length === 0) return null;

  const sorted = [...invoices].sort((a, b) => {
    const aPaid = String(a?.status || "").toUpperCase() === "PAID";
    const bPaid = String(b?.status || "").toUpperCase() === "PAID";
    if (aPaid !== bPaid) return bPaid ? 1 : -1;

    const aNo = Number(a?.invoiceNo ?? 0);
    const bNo = Number(b?.invoiceNo ?? 0);
    if (Number.isFinite(aNo) && Number.isFinite(bNo) && aNo !== bNo) {
      return bNo - aNo;
    }

    const aTotal = Number(a?.grandTotal ?? 0);
    const bTotal = Number(b?.grandTotal ?? 0);
    if (Number.isFinite(aTotal) && Number.isFinite(bTotal) && aTotal !== bTotal) {
      return bTotal - aTotal;
    }

    return 0;
  });

  return sorted[0]?.invoiceId || null;
};

const resolvePayFirstInvoiceId = (order) => {
  const invoices = Array.isArray(order?.invoices) ? order.invoices : [];
  if (invoices.length === 0) return null;

  const sorted = [...invoices].sort((a, b) => {
    const aOpen = String(a?.status || "").toUpperCase() === "OPEN";
    const bOpen = String(b?.status || "").toUpperCase() === "OPEN";
    if (aOpen !== bOpen) return bOpen ? 1 : -1;

    const aNo = Number(a?.invoiceNo ?? 0);
    const bNo = Number(b?.invoiceNo ?? 0);
    if (Number.isFinite(aNo) && Number.isFinite(bNo) && aNo !== bNo) {
      return bNo - aNo;
    }

    return 0;
  });

  return sorted[0]?.invoiceId || null;
};

const getOrderPaidAmount = (orderSnapshot) => {
  if (!orderSnapshot) return 0;

  const invoicePaidTotal = Array.isArray(orderSnapshot?.invoices)
    ? orderSnapshot.invoices.reduce(
        (sum, invoice) => sum + Number(invoice?.paidAmount || 0),
        0,
      )
    : 0;

  const totalsPaid = Number(orderSnapshot?.totals?.paid);
  if (Number.isFinite(totalsPaid) && totalsPaid > 0) {
    return totalsPaid;
  }

  const paidAmount = Number(orderSnapshot?.paidAmount);
  if (Number.isFinite(paidAmount) && paidAmount > 0) {
    return paidAmount;
  }

  return invoicePaidTotal;
};

const getRemainingBalance = (orderSnapshot, fallbackGrandTotal = 0, fallbackPaid = 0) => {
  const grandTotal = Number(
    orderSnapshot?.totals?.grandTotal ?? fallbackGrandTotal ?? 0,
  );
  const paid = getOrderPaidAmount(orderSnapshot) || Number(fallbackPaid || 0);

  if (!Number.isFinite(grandTotal) || grandTotal <= 0) {
    return 0;
  }

  return Math.max(0, grandTotal - paid);
};

const toPositiveInt = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.trunc(parsed));
};

const getModifierSignature = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "";

  return modifiers
    .map((modifier) => {
      const optionId =
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        "";
      const fallbackName = String(
        modifier?.name || modifier?.modifierName || modifier?.optionName || "",
      )
        .trim()
        .toLowerCase();
      const fallbackPrice = Number(
        modifier?.priceDelta ?? modifier?.price ?? 0,
      ).toFixed(2);
      const identity =
        optionId || (fallbackName ? `${fallbackName}@${fallbackPrice}` : "");
      const qty = toPositiveInt(modifier?.qty);
      return identity ? `${identity}:${qty}` : "";
    })
    .filter(Boolean)
    .sort()
    .join("|");
};

const getModifiersTotalPrice = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return 0;

  return modifiers.reduce((total, modifier) => {
    const priceDelta = Number(modifier?.priceDelta || modifier?.price || 0);
    const qty = toPositiveInt(modifier?.qty || 1);
    return total + priceDelta * qty;
  }, 0);
};

const getLineSubtotalValue = (line) =>
  Number(
    line?.lineSubtotal ??
      line?.lineSubTotal ??
      line?.subtotal ??
      line?.lineTotal ??
      0,
  );

const getEffectiveUnitPrice = (line) => {
  const basePrice = Number(line?.unitPrice || 0);
  const modifiersTotal = getModifiersTotalPrice(line?.modifiers);
  const qty = Math.max(0, Number(line?.qty || 0));
  const subtotal = getLineSubtotalValue(line);

  // Prefer backend-calculated unit price when available to avoid
  // double counting modifier prices across payload variants.
  if (qty > 0 && subtotal > 0) {
    const inferredUnitPrice = subtotal / qty;
    const expectedWithModifiers = basePrice + modifiersTotal;

    if (Math.abs(inferredUnitPrice - basePrice) < 0.01) {
      return basePrice;
    }

    if (Math.abs(inferredUnitPrice - expectedWithModifiers) < 0.01) {
      return expectedWithModifiers;
    }

    return inferredUnitPrice;
  }

  return basePrice + modifiersTotal;
};

const getModifierDisplayText = (modifiers = []) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "";

  return [...modifiers]
    .map((modifier) => ({
      id:
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        "",
      name:
        modifier?.name ||
        modifier?.modifierName ||
        modifier?.optionName ||
        "Modifier",
    }))
    .sort((a, b) => {
      const nameDiff = a.name.localeCompare(b.name);
      if (nameDiff !== 0) return nameDiff;
      return String(a.id).localeCompare(String(b.id));
    })
    .map((modifier) => modifier.name)
    .join(", ");
};

const splitModifiersIntoLineItems = (modifiers = []) => {
  const normalizedModifiers = Array.isArray(modifiers)
    ? modifiers.filter(Boolean)
    : [];

  if (normalizedModifiers.length <= 1) {
    return [normalizedModifiers];
  }

  return normalizedModifiers.map((modifier) => [modifier]);
};

const getLineGroupingKey = (line) => {
  const lineItemId = line?.itemId || line?.productId || "";
  const lineName = String(line?.itemName || line?.productName || "")
    .trim()
    .toLowerCase();
  const stationCode = String(line?.stationCode || "")
    .trim()
    .toUpperCase();
  const identity = lineItemId || `${lineName}::${stationCode}`;

  return `${identity}::${getModifierSignature(line?.modifiers)}`;
};

const getResolvedQtySentToKitchen = (line, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  const reportedQtySent = Math.max(
    0,
    Math.min(qty, getNumeric(line?.qtySentToKitchen)),
  );

  if (reportedQtySent > 0) return reportedQtySent;
  if (line?.isSentToKitchen) return qty;
  return 0;
};

const groupDisplayLines = (lines = [], getNumeric, getLineDisplaySubtotal) => {
  const grouped = new Map();

  lines.forEach((line) => {
    const key = getLineGroupingKey(line);
    const existing = grouped.get(key);
    const resolvedQtySent = getResolvedQtySentToKitchen(line, getNumeric);

    if (existing) {
      existing.qty += getNumeric(line.qty);
      existing.lineSubtotal += getLineDisplaySubtotal(line);
      existing.qtySentToKitchen += resolvedQtySent;
      existing.isSentToKitchen =
        existing.isSentToKitchen || Boolean(line?.isSentToKitchen);
      existing.sourceLines.push(line);
      return;
    }

    grouped.set(key, {
      ...line,
      qty: getNumeric(line.qty),
      lineSubtotal: getLineDisplaySubtotal(line),
      qtySentToKitchen: resolvedQtySent,
      isSentToKitchen:
        Boolean(line?.isSentToKitchen) || resolvedQtySent > 0,
      sourceLines: [line],
    });
  });

  return Array.from(grouped.values());
};

const getKitchenStatus = (line, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  const qtySent = getResolvedQtySentToKitchen(line, getNumeric);

  if (qtySent === 0) {
    return {
      code: "NOT_SENT",
      label: "Not sent",
      tone: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
      helper: "Not sent to kitchen yet",
      qtyPending: qty,
      qtySent,
    };
  }

  if (qtySent >= qty) {
    return {
      code: "SENT",
      label: "Sent",
      tone: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
      helper: "Sent to kitchen",
      qtyPending: 0,
      qtySent,
    };
  }

  return {
    code: "PARTIALLY_SENT",
    label: "Partially sent",
    tone: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
    helper: `${qtySent} of ${qty} sent to kitchen`,
    qtyPending: qty - qtySent,
    qtySent,
  };
};

const getSplitLineSubtotal = (line, qtyPortion, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  if (qty <= 0) return 0;
  const subtotal = getNumeric(
    line?.lineSubtotal ??
      line?.lineSubTotal ??
      line?.subtotal ??
      line?.lineTotal,
  );
  return (subtotal / qty) * qtyPortion;
};

const getSplitLineTotal = (line, qtyPortion, getNumeric) => {
  const qty = Math.max(0, getNumeric(line?.qty));
  if (qty <= 0) return 0;
  const total = getNumeric(
    line?.lineTotal ??
      line?.total ??
      line?.lineSubtotal ??
      line?.lineSubTotal ??
      line?.subtotal,
  );
  return (total / qty) * qtyPortion;
};

const normalizeLineModifiersForPayload = (modifiers = []) => {
  if (!Array.isArray(modifiers)) return [];

  return modifiers
    .map((modifier) => ({
      modifierId:
        modifier?.modifierId ||
        modifier?.optionId ||
        modifier?.modifierOptionId ||
        modifier?.productModifierOptionId ||
        modifier?.modifierItemId ||
        null,
      qty: toPositiveInt(modifier?.qty, 1),
    }))
    .filter((modifier) => Boolean(modifier.modifierId));
};

const getModifierOptionLookupKey = (name, price) => {
  const normalizedName = String(name || "")
    .trim()
    .toLowerCase();
  const normalizedPrice = Number(price ?? 0).toFixed(2);
  return `${normalizedName}::${normalizedPrice}`;
};

const resolvePerUnitModifierQty = (modifierQty, lineQty) => {
  const normalizedModifierQty = toPositiveInt(modifierQty, 1);
  const normalizedLineQty = Math.max(1, toPositiveInt(lineQty, 1));

  // Some backends return modifier qty aggregated at line level.
  // Convert back to per-unit qty when it is an exact multiple.
  if (
    normalizedLineQty > 1 &&
    normalizedModifierQty >= normalizedLineQty &&
    normalizedModifierQty % normalizedLineQty === 0
  ) {
    return Math.max(1, normalizedModifierQty / normalizedLineQty);
  }

  return normalizedModifierQty;
};

const resolveLineModifiersForPayload = (
  lineModifiers = [],
  modifierOptions = [],
  lineQty = 1,
) => {
  const normalized = normalizeLineModifiersForPayload(lineModifiers);
  if (normalized.length > 0) {
    return normalized.map((modifier) => ({
      ...modifier,
      qty: resolvePerUnitModifierQty(modifier.qty, lineQty),
    }));
  }

  if (!Array.isArray(lineModifiers) || lineModifiers.length === 0) return [];

  const optionByKey = new Map();
  (Array.isArray(modifierOptions) ? modifierOptions : []).forEach((option) => {
    const optionId =
      option?.id || option?.optionId || option?.modifierOptionId || null;
    if (!optionId) return;

    optionByKey.set(
      getModifierOptionLookupKey(
        option?.name || option?.displayName || "",
        option?.priceDelta ?? option?.price ?? 0,
      ),
      optionId,
    );
  });

  return lineModifiers
    .map((modifier) => {
      const resolvedId = optionByKey.get(
        getModifierOptionLookupKey(
          modifier?.name || modifier?.modifierName || modifier?.optionName || "",
          modifier?.priceDelta ?? modifier?.price ?? 0,
        ),
      );
      return resolvedId
        ? {
            modifierId: resolvedId,
            qty: resolvePerUnitModifierQty(modifier?.qty, lineQty),
          }
        : null;
    })
    .filter(Boolean);
};

const buildLineQtyUpdatePayload = (line, qty) => {
  const payload = { qty };
  const modifiers = normalizeLineModifiersForPayload(line?.modifiers);
  if (modifiers.length > 0) {
    payload.modifiers = modifiers;
  }
  return payload;
};

const buildModifierQtyPatchPayload = ({ line, nextQty, modifierOptions }) => {
  const baseModifiers = resolveLineModifiersForPayload(
    line?.modifiers,
    modifierOptions,
    line?.qty,
  );

  return {
    qty: nextQty,
    modifiers: baseModifiers,
  };
};

const getPendingCustomerStorageKey = (orderId) =>
  `pos.pendingCustomer.${orderId}`;

const OrderEditor = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const seatIdFromNav = location.state?.seatId || null;
  const seatIdFromStorage = orderId
    ? sessionStorage.getItem(`pos.orderSeatId.${orderId}`)
    : null;

  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showDiscount, setShowDiscount] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showModifiers, setShowModifiers] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [isRefreshingAfterPayment, setIsRefreshingAfterPayment] =
    useState(false);
  const [activePaymentMethod, setActivePaymentMethod] = useState(null);
  const [paymentProgress, setPaymentProgress] = useState({
    title: "Finalizing payment...",
    message: "Please wait while we complete the payment.",
    value: 20,
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null,
  });
  const [closePendingModalOpen, setClosePendingModalOpen] = useState(false);
  const [stationRequiredModalOpen, setStationRequiredModalOpen] = useState(false);
  const [hasSubmittedChanges, setHasSubmittedChanges] = useState(false);
  const releaseAttemptedRef = useRef(false);
  const draftSyncPromiseRef = useRef(null);
  const draftSyncTimeoutRef = useRef(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [modifierPreset, setModifierPreset] = useState([]);
  const [skipUpsellForSelectedItem, setSkipUpsellForSelectedItem] =
    useState(false);
  const [notes, setNotes] = useState("");
  const [realtimeUpdate, setRealtimeUpdate] = useState(null);
  const [orderDuration, setOrderDuration] = useState("");
  const [upsellModal, setUpsellModal] = useState({
    isOpen: false,
    triggerItemName: "",
    triggerItemPrice: null,
    suggestions: [],
  });
  const [categoryFilter, setCategoryFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const lastInvoicePaymentAttemptRef = useRef(null);

  // Get POS payment mode setting
  const { posPaymentMode, tipConfig } = useStationFeature();
  const cashSessionGuard = useSubmitCashSessionGuard();

  // Calculate order duration
  const calculateDuration = (createdAt) => {
    if (!createdAt) return "";
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) {
      return `${diffMins} min`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  // Real-time updates for this order
  useOrderRealtime(orderId, (payload) => {
    setRealtimeUpdate(payload);
    setTimeout(() => setRealtimeUpdate(null), 3000);
  });

  // Fetch order
  const {
    data: order,
    isLoading,
    error: orderError,
    refetch,
  } = useGetPosOrderQuery(orderId, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const { data: upsellCampaignsData } = useGetUpsellCampaignsQuery();
  const { data: referenceData } = useGetCatalogReferenceQuery();
  const [localDraftLines, setLocalDraftLines] = useState([]);
  const [orderOverride, setOrderOverride] = useState(null);
  const [showPostSubmitProcessing, setShowPostSubmitProcessing] = useState(false);
  const effectiveOrder = orderOverride || order;
  const effectiveOrderLines = React.useMemo(
    () => effectiveOrder?.lines || [],
    [effectiveOrder],
  );
  const { data: taxRatesData } = useGetTaxRatesQuery();
  const draftTaxRate = React.useMemo(() => {
    const totals = effectiveOrder?.totals || {};
    const subTotal = Number(totals.subTotal || 0);
    const tax = Number(totals.tax || 0);
    return subTotal > 0 && tax >= 0 ? tax / subTotal : 0;
  }, [effectiveOrder]);
  const menuOrderType = normalizeOrderTypeForEligibility(
    effectiveOrder?.orderType || order?.orderType || "DINEIN",
  );
  const {
    data: menuData,
    isLoading: isMenuLoading,
    refetch: refetchMenu,
  } = useGetPublicCatalogMenuQuery({
    orderType: menuOrderType,
  });

  React.useEffect(() => {
    if (!orderId) return;

    setOrderOverride(null);
    setLocalDraftLines([]);
  }, [orderId]);

  React.useEffect(() => {
    if (!orderId) {
      setLocalDraftLines([]);
      setOrderOverride(null);
      return;
    }

    const storedDraft = readLocalDraft(orderId);

    const serverHasPendingLines = effectiveOrderLines.some((line) => {
      const qty = Number(line?.qty || 0);
      const qtySent = Number(
        line?.qtySentToKitchen || (line?.isSentToKitchen ? qty : 0),
      );
      return qty > qtySent;
    });

    if (!serverHasPendingLines) {
      if (Array.isArray(storedDraft) && storedDraft.length > 0) {
        setLocalDraftLines(storedDraft);
        return;
      }
      clearLocalDraft(orderId);
      setLocalDraftLines([]);
      return;
    }
    if (storedDraft !== null) {
      setLocalDraftLines(storedDraft);
      return;
    }

    setLocalDraftLines(buildDraftStateFromOrder(effectiveOrderLines));
  }, [orderId, effectiveOrderLines]);

  React.useEffect(() => {
    if (!orderId) return;
    writeLocalDraft(orderId, localDraftLines);
  }, [localDraftLines, orderId]);

  const buildDraftSyncSignature = React.useCallback((lines = []) => {
    const normalized = (Array.isArray(lines) ? lines : [])
      .map((line) => ({
        serverLineId: String(line?.__serverLineId || ""),
        itemId: String(line?.itemId || line?.productId || ""),
        qty: Number(line?.qty || 0),
        notes: String(line?.notes || ""),
        modifiers: JSON.stringify(line?.modifiers || []),
      }))
      .sort((a, b) =>
        `${a.serverLineId}:${a.itemId}:${a.modifiers}`.localeCompare(
          `${b.serverLineId}:${b.itemId}:${b.modifiers}`,
        ),
      );

    return JSON.stringify(normalized);
  }, []);

  const serverDraftSignature = React.useMemo(
    () => buildDraftSyncSignature(buildDraftStateFromOrder(effectiveOrderLines)),
    [buildDraftSyncSignature, effectiveOrderLines],
  );
  const localDraftSignature = React.useMemo(
    () => buildDraftSyncSignature(localDraftLines),
    [buildDraftSyncSignature, localDraftLines],
  );

  React.useEffect(() => {
    const refreshMenu = () => {
      refetchMenu();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshMenu();
      }
    };

    window.addEventListener("focus", refreshMenu);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const interval = setInterval(refreshMenu, 60000);

    return () => {
      window.removeEventListener("focus", refreshMenu);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      clearInterval(interval);
    };
  }, [refetchMenu]);

  // Mutations
  const [addLine] = useAddPosOrderLineMutation();
  const [updateNotes, { isLoading: isUpdatingNotes }] =
    useUpdatePosOrderNotesMutation();
  const [submitOrder, { isLoading: isSubmitting }] =
    useSubmitPosOrderMutation();
  const [
    processOrderPayment,
    { isLoading: isProcessingOrderPayment, error: orderPaymentError },
  ] = useProcessPosOrderPaymentMutation();
  const [
    processInvoicePayment,
    { isLoading: isProcessingInvoicePayment, error: invoicePaymentError },
  ] = useProcessPosInvoicePaymentMutation();
  const [closeOrder, { isLoading: isClosingOrder }] =
    useClosePosOrderMutation();
  const [updateOrderCustomer] = useUpdatePosOrderCustomerMutation();
  const [updateLine] = useUpdatePosOrderLineMutation();
  const [deleteLine] = useDeletePosOrderLineMutation();
  const [sendSeatHeartbeat] = usePosSeatHeartbeatMutation();
  const [releaseSeat] = usePosSeatReleaseMutation();
  const [fetchOrderInvoices] = useLazyGetPosOrderInvoicesQuery();
  const dispatch = useDispatch();
  const invokeAddLine = React.useCallback(
    ({ orderId: targetOrderId, payload }) =>
      addLine({
        orderId: targetOrderId,
        payload,
      }).unwrap(),
    [addLine],
  );
  const invokeUpdateLine = React.useCallback(
    ({ orderId: targetOrderId, lineId, payload }) =>
      updateLine({
        orderId: targetOrderId,
        lineId,
        payload,
      }).unwrap(),
    [updateLine],
  );
  const invokeDeleteLine = React.useCallback(
    ({ orderId: targetOrderId, lineId }) =>
      deleteLine({
        orderId: targetOrderId,
        lineId,
      }).unwrap(),
    [deleteLine],
  );

  const isProcessingPayment =
    isProcessingOrderPayment || isProcessingInvoicePayment;
  const paymentError = invoicePaymentError || orderPaymentError;

  // Handle order error
  React.useEffect(() => {
    if (orderError) {
      const message = getPosErrorMessage(orderError);
      setError(message);

      if (shouldNavigateToSeatBoard(orderError)) {
        setTimeout(() => navigate("/pos"), 2000);
      }
    }
  }, [orderError, navigate]);

  // Initialize notes from order
  React.useEffect(() => {
    if (effectiveOrder?.notes) {
      setNotes(effectiveOrder.notes);
    }
  }, [effectiveOrder]);

  const resolvedSeatId =
    effectiveOrder?.seatId || order?.seatId || seatIdFromNav || seatIdFromStorage;

  // Keep seat session alive while editor is open
  React.useEffect(() => {
    if (!resolvedSeatId) return undefined;

    let isActive = true;
    const heartbeat = () => {
      if (!isActive) return;
      sendSeatHeartbeat(resolvedSeatId).unwrap().catch(() => {});
    };

    heartbeat();
    const interval = setInterval(heartbeat, 25000);

    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [resolvedSeatId, effectiveOrder?.status, sendSeatHeartbeat]);

  // Update order duration every minute
  React.useEffect(() => {
    if (effectiveOrder?.createdAt) {
      setOrderDuration(calculateDuration(effectiveOrder.createdAt));

      const interval = setInterval(() => {
        setOrderDuration(calculateDuration(effectiveOrder.createdAt));
      }, 60000);

      return () => clearInterval(interval);
    }
  }, [effectiveOrder?.createdAt]);

  const upsellCampaigns = React.useMemo(
    () => (Array.isArray(upsellCampaignsData) ? upsellCampaignsData : []),
    [upsellCampaignsData],
  );
  const allItems = React.useMemo(
    () => (Array.isArray(menuData?.items) ? menuData.items : []),
    [menuData?.items],
  );
  const categories = React.useMemo(
    () => (Array.isArray(menuData?.categories) ? menuData.categories : []),
    [menuData?.categories],
  );
  const activeCategories = React.useMemo(
    () => categories.filter((category) => category?.isActive !== false),
    [categories],
  );
  const activeCategoryIds = React.useMemo(
    () => new Set(activeCategories.map((category) => String(category.id))),
    [activeCategories],
  );
  const activeParentCategories = React.useMemo(
    () =>
      activeCategories
        .filter((category) => !category.parentCategoryId)
        .sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [activeCategories],
  );
  const categoriesById = React.useMemo(() => {
    const map = new Map();
    categories.forEach((category) => {
      if (category?.id) {
        map.set(String(category.id), category);
      }
    });
    return map;
  }, [categories]);
  const modifierOptions = React.useMemo(
    () =>
      Array.isArray(referenceData?.modifierOptions)
        ? referenceData.modifierOptions
        : [],
    [referenceData?.modifierOptions],
  );
  const itemsById = React.useMemo(() => {
    const map = new Map();
    allItems.forEach((item) => {
      if (item?.id) {
        map.set(item.id, item);
      }
    });
    return map;
  }, [allItems]);

  const filteredItems = React.useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const hasCategoryMetadata = categories.length > 0;
    const normalizedCategoryFilter = String(categoryFilter || "");
    const selectedCategory = normalizedCategoryFilter
      ? categoriesById.get(normalizedCategoryFilter) || null
      : null;
    const childCategoryIds = new Set(
      activeCategories
        .filter(
          (category) =>
            String(category?.parentCategoryId || "") === normalizedCategoryFilter,
        )
        .map((category) => String(category.id)),
    );
    const hasChildCategories =
      normalizedCategoryFilter &&
      !selectedCategory?.parentCategoryId &&
      childCategoryIds.size > 0;

    return allItems
      .filter((item) => isItemEligibleForOrderType(item, menuOrderType))
      .filter((item) =>
        hasCategoryMetadata
          ? activeCategoryIds.has(String(item?.categoryId || ""))
          : true,
      )
      .filter((item) =>
        normalizedCategoryFilter
          ? hasChildCategories
            ? String(item?.categoryId || "") === normalizedCategoryFilter ||
              childCategoryIds.has(String(item?.categoryId || ""))
            : item.categoryId === categoryFilter
          : true,
      )
      .filter((item) => {
        if (!query) return true;
        const haystack =
          `${item.name || ""} ${item.description || ""}`.toLowerCase();
        return haystack.includes(query);
      });
  }, [
    allItems,
    categoryFilter,
    searchQuery,
    activeCategoryIds,
    activeCategories,
    categoriesById,
    categories.length,
    menuOrderType,
  ]);

  const visibleCategoryIds = React.useMemo(() => {
    const ids = new Set();
    allItems
      .filter((item) => isItemEligibleForOrderType(item, menuOrderType))
      .filter((item) =>
        categories.length > 0
          ? activeCategoryIds.has(String(item?.categoryId || ""))
          : true,
      )
      .forEach((item) => {
        if (item?.categoryId) {
          ids.add(String(item.categoryId));
        }
      });
    return ids;
  }, [allItems, activeCategoryIds, categories.length, menuOrderType]);
  const visibleParentCategoryIds = React.useMemo(() => {
    const ids = new Set();
    visibleCategoryIds.forEach((categoryId) => {
      ids.add(String(categoryId));
      const parentCategoryId = categoriesById.get(String(categoryId))?.parentCategoryId;
      if (parentCategoryId) {
        ids.add(String(parentCategoryId));
      }
    });
    activeCategories.forEach((category) => {
      if (category?.parentCategoryId) {
        ids.add(String(category.parentCategoryId));
      }
    });
    return ids;
  }, [activeCategories, categoriesById, visibleCategoryIds]);

  const visibleParentCategories = React.useMemo(
    () =>
      activeParentCategories.filter((category) =>
        visibleParentCategoryIds.has(String(category.id)),
      ),
    [activeParentCategories, visibleParentCategoryIds],
  );
  const selectedCategory = React.useMemo(
    () => categoriesById.get(String(categoryFilter || "")) || null,
    [categoriesById, categoryFilter],
  );
  const activeRailCategoryId =
    selectedCategory?.parentCategoryId || categoryFilter;
  const visibleChildCategories = React.useMemo(
    () =>
      activeCategories
        .filter(
          (category) =>
            String(category?.parentCategoryId || "") === String(categoryFilter || ""),
        )
        .sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [activeCategories, categoryFilter],
  );
  const selectedParentItems = React.useMemo(
    () =>
      !searchQuery &&
      categoryFilter &&
      !selectedCategory?.parentCategoryId
        ? filteredItems.filter(
            (item) => String(item?.categoryId || "") === String(categoryFilter),
          )
        : [],
    [categoryFilter, filteredItems, searchQuery, selectedCategory],
  );
  const showParentCategoryBrowser =
    !searchQuery &&
    !!categoryFilter &&
    !selectedCategory?.parentCategoryId &&
    (visibleChildCategories.length > 0 || selectedParentItems.length > 0);
  const showChildCategorySection =
    showParentCategoryBrowser && visibleChildCategories.length > 0;
  const showParentItemSection =
    showParentCategoryBrowser && selectedParentItems.length > 0;
  const useCategorySectionHeading =
    showChildCategorySection && showParentItemSection;
  const useItemSectionHeading =
    showChildCategorySection && showParentItemSection;
  const visibleSelectableCategoryIds = React.useMemo(() => {
    const ids = new Set(visibleParentCategoryIds);
    visibleChildCategories.forEach((category) => {
      ids.add(String(category.id));
    });
    if (selectedCategory?.id) {
      ids.add(String(selectedCategory.id));
    }
    if (selectedCategory?.parentCategoryId) {
      ids.add(String(selectedCategory.parentCategoryId));
    }
    return ids;
  }, [selectedCategory, visibleChildCategories, visibleParentCategoryIds]);

  React.useEffect(() => {
    if (categoryFilter && !visibleSelectableCategoryIds.has(String(categoryFilter))) {
      setCategoryFilter("");
    }
  }, [categoryFilter, visibleSelectableCategoryIds]);

  const selectedItemModifierGroups = React.useMemo(
    () =>
      resolveItemModifierGroups(
        selectedItem?.modifierGroups || [],
        modifierOptions,
      ),
    [selectedItem, modifierOptions],
  );

  const isLineNonTaxable = React.useCallback(
    (line) => {
      if (line?.isTaxable === false || line?.taxable === false) return true;
      const itemId = line?.itemId || line?.productId || null;
      if (!itemId) return false;
      const item = itemsById.get(itemId);
      return item?.isTaxable === false || item?.taxable === false;
    },
    [itemsById],
  );
  const activeTaxRate = React.useMemo(
    () =>
      (Array.isArray(taxRatesData) ? taxRatesData : [])
        .filter((taxRate) => taxRate?.isActive !== false)
        .reduce(
          (sum, taxRate) =>
            sum + toRateFraction(taxRate?.ratePercent ?? taxRate?.rate ?? 0),
          0,
        ),
    [taxRatesData],
  );

  const openUpsellSuggestions = React.useCallback(
    (triggerItem) => {
      if (!triggerItem?.id) return;

      const matchedCampaigns = upsellCampaigns
        .filter(
          (campaign) =>
            campaign?.isActive &&
            Array.isArray(campaign.triggerItemIds) &&
            campaign.triggerItemIds.includes(triggerItem.id),
        )
        .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));

      if (matchedCampaigns.length === 0) return;

      const seenIds = new Set();
      const suggestions = [];

      for (const campaign of matchedCampaigns) {
        const orderedUpsells = Array.isArray(campaign.upsellItems)
          ? [...campaign.upsellItems].sort(
              (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0),
            )
          : [];

        for (const upsellItem of orderedUpsells) {
          const itemId = upsellItem?.itemId;
          if (
            !itemId ||
            itemId === triggerItem.id ||
            seenIds.has(itemId) ||
            !itemsById.has(itemId)
          ) {
            continue;
          }

          const item = itemsById.get(itemId);
          if (item?.isAvailable === false) continue;

          suggestions.push(item);
          seenIds.add(itemId);

          if (suggestions.length >= 6) break;
        }

        if (suggestions.length >= 6) break;
      }

      if (suggestions.length === 0) return;

      setUpsellModal({
        isOpen: true,
        triggerItemName: triggerItem.name || "",
        triggerItemPrice:
          Number.isFinite(Number(triggerItem.basePrice))
            ? Number(triggerItem.basePrice)
            : null,
        suggestions,
      });
    },
    [itemsById, upsellCampaigns],
  );

  const addItemToOrder = async (
    item,
    modifiers = [],
    { showUpsell = true } = {},
  ) => {
    if (!item?.id) return;

    try {
      setLocalDraftLines((currentLines) =>
        addItemToLocalDraft({
          draftLines: currentLines,
          item,
          modifiers,
          fallbackTaxRate: draftTaxRate,
        }),
      );

      setSelectedItem(null);
      setShowModifiers(false);

      if (showUpsell) {
        openUpsellSuggestions(item);
      }
    } catch (err) {
      if (isItemStationRequiredError(err)) {
        setStationRequiredModalOpen(true);
        return;
      }
      setError(getPosErrorMessage(err));
    }
  };

  const handleItemSelect = (item) => {
    if (item?.isAvailable === false) return;

    setSelectedItem(item);
    setModifierPreset([]);
    setSkipUpsellForSelectedItem(false);

    if (itemHasSelectableModifiers(item.modifierGroups, modifierOptions)) {
      setShowModifiers(true);
    } else {
      addItemToOrder(item, [], { showUpsell: true });
    }
  };

  const handleAddItem = async (modifiers) => {
    if (!selectedItem) return;

    const modifierLineItems = splitModifiersIntoLineItems(modifiers);

    for (let index = 0; index < modifierLineItems.length; index += 1) {
      await addItemToOrder(selectedItem, modifierLineItems[index], {
        showUpsell: index === 0 ? !skipUpsellForSelectedItem : false,
      });
    }

    setSkipUpsellForSelectedItem(false);
  };

  const handleConfirmUpsellItems = async (items = []) => {
    setUpsellModal({
      isOpen: false,
      triggerItemName: "",
      triggerItemPrice: null,
      suggestions: [],
    });

    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    for (const entry of items) {
      const item = entry?.item;
      const qty = Math.max(1, Number(entry?.qty || 1));
      if (!item) continue;

      for (let i = 0; i < qty; i += 1) {
        await addItemToOrder(item, [], { showUpsell: false });
      }
    }
  };

  const handleUpdateNotes = async () => {
    try {
      await updateNotes({
        orderId,
        payload: { notes },
      }).unwrap();
      setShowNotesModal(false);
    } catch (err) {
      const title = err?.data?.title;
      if (err?.status === 409 && title === "ITEM_NOT_ALLOWED_FOR_ORDER_TYPE") {
        setError(
          err?.data?.detail ||
            "This item is not available for this order type.",
        );
        refetchMenu();
        return;
      }
      setError(getPosErrorMessage(err));
    }
  };

  const releaseSeatLock = async () => {
    if (!resolvedSeatId || releaseAttemptedRef.current) return;
    releaseAttemptedRef.current = true;
    try {
      await releaseSeat(resolvedSeatId).unwrap();
    } catch {
      // ignore release errors; seat will timeout server-side if needed
    } finally {
      if (orderId) {
        sessionStorage.removeItem(`pos.orderSeatId.${orderId}`);
      }
    }
  };

  const getPendingCustomerFromStorage = () => {
    if (!orderId) return null;
    try {
      const raw = sessionStorage.getItem(getPendingCustomerStorageKey(orderId));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const syncOrderCustomerDetails = async () => {
    if (!orderId) return effectiveOrder;

    const pendingCustomer = getPendingCustomerFromStorage();
    const name = String(
      effectiveOrder?.customerName || pendingCustomer?.name || "",
    ).trim();
    const phone = String(
      effectiveOrder?.customerPhone || pendingCustomer?.phone || "",
    ).trim();
    const email = String(
      effectiveOrder?.customerEmail || pendingCustomer?.email || "",
    ).trim();

    if (!name && !phone && !email) return effectiveOrder;

    await updateOrderCustomer({
      orderId,
      payload: {
        customerName: name || undefined,
        customerPhone: phone || undefined,
        customerEmail: email || undefined,
      },
    }).unwrap();

    const updatedOrder = {
      ...effectiveOrder,
      customerName: name || undefined,
      customerPhone: phone || undefined,
      customerEmail: email || undefined,
    };
    setOrderOverride(updatedOrder || null);
    return updatedOrder;
  };

  const syncPendingDraftLines = React.useCallback(async (options = {}) => {
    if (!orderId) return effectiveOrder;

    if (draftSyncTimeoutRef.current) {
      clearTimeout(draftSyncTimeoutRef.current);
      draftSyncTimeoutRef.current = null;
    }

    if (draftSyncPromiseRef.current) {
      return await draftSyncPromiseRef.current;
    }

    const targetDraftLines = options.draftLines ?? localDraftLines;
    const runSync = (async () => {
      const syncedOrder = await syncLocalDraftToServer({
        orderId,
        orderLines: effectiveOrderLines,
        draftLines: targetDraftLines,
        addLine: invokeAddLine,
        updateLine: invokeUpdateLine,
        deleteLine: invokeDeleteLine,
        buildLineQtyUpdatePayload,
        buildModifierQtyPatchPayload,
        modifierOptions,
      });

      const refreshed = syncedOrder ? null : await refetch();
      const updatedOrder = syncedOrder || refreshed?.data || effectiveOrder;
      const nextDraftLines = buildDraftStateFromOrder(updatedOrder?.lines || []);
      setOrderOverride(updatedOrder || null);
      setLocalDraftLines(nextDraftLines);
      if (nextDraftLines.length === 0) {
        clearLocalDraft(orderId);
      }
      return updatedOrder;
    })();

    draftSyncPromiseRef.current = runSync;

    try {
      return await runSync;
    } finally {
      if (draftSyncPromiseRef.current === runSync) {
        draftSyncPromiseRef.current = null;
      }
    }
  }, [
    effectiveOrder,
    orderId,
    effectiveOrderLines,
    localDraftLines,
    invokeAddLine,
    invokeUpdateLine,
    invokeDeleteLine,
    refetch,
    modifierOptions,
  ]);

  const refetchLatestOrder = React.useCallback(async () => {
    const refreshed = await refetch();
    const latestOrder = refreshed?.data || null;
    if (latestOrder) {
      setOrderOverride(latestOrder);
    }
    return latestOrder;
  }, [refetch]);

  const runWithStaleRetry = React.useCallback(
    async (operation, options = {}) => {
      const { attempts = 3, pauseMs = 250 } = options;
      let lastError = null;

      for (let attempt = 0; attempt < attempts; attempt += 1) {
        try {
          return await operation();
        } catch (err) {
          const errorCode = err?.data?.code || err?.data?.errorCode;
          if (errorCode !== POS_ERROR_CODES.CONFLICT_STALE_VERSION) {
            throw err;
          }

          lastError = err;
          await refetchLatestOrder();
          await sleep(pauseMs);
        }
      }

      throw lastError;
    },
    [refetchLatestOrder],
  );

  const handleSubmit = async () => {
    if (pendingDisplayLines.length === 0) {
      setError("No new items to submit. Please add items first.");
      return;
    }

    if (!cashSessionGuard.ensureActiveCashSession()) return;

    // Check PAY_FIRST mode requirement
    if (isPayFirstMode && pendingItemsNeedPayment) {
      setError(
        `Payment required. Please pay $${unpaidAmount.toFixed(2)} before submitting to kitchen.`,
      );
      return;
    }

    setConfirmModal({
      isOpen: true,
      action: "SUBMIT_ORDER",
    });
  };

  const handleRequestClose = () => {
    if (isDraft && pendingDisplayLines.length > 0) {
      setConfirmModal({
        isOpen: true,
        action: "CLOSE_ORDER",
      });
      return;
    }

    void handleExitToSeatBoard();
  };

  const performSubmit = async () => {
    let updatedOrder = effectiveOrder;
    setShowPostSubmitProcessing(true);

    try {
      updatedOrder = await runWithStaleRetry(() => syncPendingDraftLines());
      updatedOrder = await runWithStaleRetry(() => syncOrderCustomerDetails());
    } catch (err) {
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setError(getPosErrorMessage(err));
      return;
    }

    try {
      const submitLatestOrder = async () => {
        const latestOrder =
          (await refetchLatestOrder()) || updatedOrder || effectiveOrder || null;

        return await submitOrder({
          orderId,
          payload: {
            expectedRowVersion:
              latestOrder?.rowVersion ?? updatedOrder?.rowVersion,
            submitRequestId: generateUUID(),
          },
        }).unwrap();
      };

      const result = await runWithStaleRetry(submitLatestOrder);

      clearLocalDraft(orderId);
      const submittedOrder = buildSubmittedOrderSnapshot(
        result?.order || updatedOrder || effectiveOrder || null,
      );
      setOrderOverride(submittedOrder);
      setLocalDraftLines([]);

      setHasSubmittedChanges(true);

      const { deltaLineCount } = result;

      if (deltaLineCount > 0) {
        setSuccessMessage(
          `Sent ${deltaLineCount} item change${deltaLineCount !== 1 ? "s" : ""} to kitchen`,
        );
      } else {
        setSuccessMessage("No new kitchen changes");
      }

      // After submitting to kitchen, return to Dining Room Control.
      setTimeout(() => {
        setSuccessMessage(null);
        setShowPostSubmitProcessing(false);
        cartLeaveGuard.allowNextNavigation();
        navigate("/pos");
      }, 1500);
    } catch (err) {
      setShowPostSubmitProcessing(false);
      const errorCode = err?.data?.code || err?.data?.errorCode;
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }

      if (isItemStationRequiredError(err)) {
        setStationRequiredModalOpen(true);
        setShowPostSubmitProcessing(false);
        return;
      }

      if (isPaymentConfirmationRequiredError(err)) {
        setError(
          "Payment confirmation is required before submitting to kitchen. Please complete the payment confirmation and try again.",
        );
        setShowPostSubmitProcessing(false);
        return;
      }

      if (errorCode === POS_ERROR_CODES.CONFLICT_STALE_VERSION) {
        const refreshed = await refetch();
        const latestOrder = refreshed?.data || null;
        if (latestOrder) {
          setOrderOverride(latestOrder);
          setLocalDraftLines(buildDraftStateFromOrder(latestOrder.lines || []));
        }
        setShowPostSubmitProcessing(false);
        setError(getPosErrorMessage(err));
        return;
      }

      setShowPostSubmitProcessing(false);
      setError(getPosErrorMessage(err));

      if (shouldNavigateToSeatBoard(err)) {
        setTimeout(() => navigate("/pos"), 2000);
      }
    }
  };

  const handleConfirmAction = async () => {
    const { action } = confirmModal;
    setConfirmModal({ isOpen: false, action: null });
    if (action === "SUBMIT_ORDER") {
      if (!cashSessionGuard.ensureActiveCashSession()) return;
      await performSubmit();
      return;
    }

    if (action === "CLOSE_ORDER") {
      await handleExitToSeatBoard();
    }
  };

  const handlePayment = async () => {
    if (!hasCartItems) return;
    if (isPayFirstMode) {
      setIsPreparingPayment(true);
      setPaymentProgress({
        title: "Processing order...",
        message: "Please wait while we prepare the order for payment.",
        value: 20,
      });
    }
    try {
      await runWithStaleRetry(() => syncPendingDraftLines());
      if (isPayFirstMode) {
        setPaymentProgress({
          title: "Processing order...",
          message: "Please wait while we validate customer details.",
          value: 60,
        });
        await runWithStaleRetry(() => syncOrderCustomerDetails());
        setPaymentProgress({
          title: "Opening payment...",
          message: "Payment options are almost ready.",
          value: 90,
        });
      }
    } catch (err) {
      setIsPreparingPayment(false);
      setError(getPosErrorMessage(err));
      return;
    }

    try {
      const refreshed = await refetch();
      const latestOrder = refreshed?.data || null;
      if (latestOrder) {
        setOrderOverride(latestOrder);
        const latestRemainingBalance = getRemainingBalance(
          latestOrder,
          grandTotalAmount,
          totalPaid,
        );
        if (latestRemainingBalance <= 0) {
          setShowPayModal(false);
          setSuccessMessage("No balance due.");
          setIsPreparingPayment(false);
          return;
        }
      }
    } catch (err) {
      setIsPreparingPayment(false);
      setError(getPosErrorMessage(err));
      return;
    }

    setIsPreparingPayment(false);
    setShowPayModal(true);
  };

  const handleProcessPayment = async (method, amount, tipAmount = 0) => {
    setActivePaymentMethod(method);
    const normalizedTipAmount = normalizeMoneyAmount(tipAmount);
    try {
      if (isPayFirstMode) {
        setIsRefreshingAfterPayment(true);
        setPaymentProgress({
          title: "Processing payment...",
          message: "Please wait while we send the payment request.",
          value: 25,
        });
        const invoices = await fetchOrderInvoices(orderId).unwrap();
        const list = Array.isArray(invoices) ? invoices : [];
        const invoiceId =
          resolvePayFirstInvoiceId({ invoices: list }) ||
          resolveReceiptInvoiceId({ invoices: list });

        if (!invoiceId) {
          setIsRefreshingAfterPayment(false);
          setError(
            "Unable to process payment because the invoice is not available yet. Please refresh and try again.",
          );
          return;
        }

        await processInvoicePayment({
          invoiceId,
          payload: {
            method,
            amount: normalizeMoneyAmount(amount),
            tipAmount: normalizedTipAmount,
          },
        }).unwrap();
        lastInvoicePaymentAttemptRef.current = null;
      } else {
        await processOrderPayment({
          orderId,
          payload: {
            method,
            amount: normalizeMoneyAmount(amount),
            tipAmount: normalizedTipAmount,
          },
        }).unwrap();
      }

      const refreshed = await refetch();
      const latestOrder = refreshed?.data || orderOverride || effectiveOrder || null;
      if (latestOrder) {
        setOrderOverride(latestOrder);
      }
      const remainingBalance = getRemainingBalance(
        latestOrder,
        grandTotalAmount,
        latestOrder ? 0 : totalPaid,
      );

      if (remainingBalance > 0) {
        setPaymentProgress({
          title: "Partial payment recorded",
          message: `Remaining balance: $${remainingBalance.toFixed(2)}. Please continue payment.`,
          value: 45,
        });
        setSuccessMessage(
          `Partial payment recorded. $${remainingBalance.toFixed(2)} remaining.`,
        );
        setIsRefreshingAfterPayment(false);
        return;
      }

      setIsRefreshingAfterPayment(true);

      // In PAY_FIRST mode, automatically submit to kitchen after payment
      if (isPayFirstMode) {
        setPaymentProgress({
          title: "Waiting for confirmation...",
          message: "Payment received. Waiting for the server to confirm it.",
          value: 55,
        });
        setSuccessMessage("Waiting for payment confirmation...");

        try {
          const invoices = await fetchOrderInvoices(orderId).unwrap();
          const list = Array.isArray(invoices) ? invoices : [];
          const invoiceId =
            resolvePayFirstInvoiceId({ invoices: list }) ||
            resolveReceiptInvoiceId({ invoices: list });

          if (!invoiceId) {
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            setError(
              "Invoice not found for this order. Payment cannot be confirmed yet. Please refresh and try again.",
            );
            return;
          }

          const confirmedInvoice = await pollInvoiceUntilPaid({
            orderId,
            invoiceId,
            fetchOrderInvoices,
            onWaiting: () => {
              setSuccessMessage("Waiting for payment confirmation...");
              setPaymentProgress({
                title: "Waiting for confirmation...",
                message:
                  "Payment received. Waiting for the server to confirm it.",
                value: 70,
              });
            },
          });

          if (confirmedInvoice.state === "PAID_EMPTY") {
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            setError(
              "Payment captured but bill is empty; cannot submit/print. Please retry or contact support.",
            );
            return;
          }

          if (confirmedInvoice.state === "TIMEOUT") {
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            setError("Payment pending; cannot submit/print.");
            return;
          }

          if (confirmedInvoice.state !== "PAID_OK") {
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            setError("Unable to confirm payment; cannot submit/print.");
            return;
          }

          setPaymentProgress({
            title: "Sending to kitchen...",
            message: "Payment is confirmed. Finishing the order now.",
            value: 88,
          });
          setSuccessMessage("Payment confirmed. Sending to kitchen...");

          const submitLatestPaidOrder = async () => {
            const refreshed = await refetch();
            const latestOrder =
              refreshed?.data || orderOverride || effectiveOrder || null;

            if (!latestOrder?.rowVersion) {
              throw new Error("Failed to refresh order data");
            }

            const result = await submitOrder({
              orderId,
              payload: {
                expectedRowVersion: latestOrder.rowVersion,
                submitRequestId: generateUUID(),
              },
            }).unwrap();

            return {
              result,
              latestOrder,
            };
          };

          let result = null;
          let latestOrderSnapshot = null;
          let staleRetryCount = 0;

          while (staleRetryCount < 3) {
            try {
              const submitOutcome = await submitLatestPaidOrder();
              result = submitOutcome.result;
              latestOrderSnapshot = submitOutcome.latestOrder;
              break;
            } catch (submitErr) {
              const errorCode =
                submitErr?.data?.code || submitErr?.data?.errorCode;
              if (errorCode !== POS_ERROR_CODES.CONFLICT_STALE_VERSION) {
                throw submitErr;
              }

              staleRetryCount += 1;
              setPaymentProgress({
                title: "Refreshing order...",
                message:
                  "Order changed on the server. Refreshing before finishing.",
                value: 90 + staleRetryCount * 3,
              });

              await sleep(300);
            }
          }

          if (!result) {
            const refreshed = await refetch();
            latestOrderSnapshot =
              refreshed?.data || orderOverride || effectiveOrder || null;

            const remainingDraftLines = buildDraftStateFromOrder(
              latestOrderSnapshot?.lines || [],
            );

            if (remainingDraftLines.length > 0) {
              throw new Error("Order is still updating. Please retry.");
            }

            result = {
              deltaLineCount: 0,
            };
          }

          setHasSubmittedChanges(true);
          clearLocalDraft(orderId);
          setLocalDraftLines([]);
          setOrderOverride(latestOrderSnapshot || null);

          const { deltaLineCount } = result;
          setSuccessMessage(
            `Submitted. Sent ${deltaLineCount} item${deltaLineCount !== 1 ? "s" : ""} to kitchen.`,
          );
          setPaymentProgress({
            title: "Done",
            message: "Payment completed. Returning to the table page.",
            value: 100,
          });

          setTimeout(() => {
            setShowPayModal(false);
            setError(null);
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            cartLeaveGuard.allowNextNavigation();
            navigate("/pos", { replace: true });
          }, 1500);
        } catch (submitErr) {
          if (isOrderingBlockedError(submitErr)) {
            dispatch(api.util.invalidateTags(["StoreFeatures"]));
          }
          const latestOrder = (await refetch())?.data || null;
          const remainingDraftLines = buildDraftStateFromOrder(
            latestOrder?.lines || [],
          );
          if (
            latestOrder &&
            (isTerminalOrderStatus(latestOrder?.status) ||
              remainingDraftLines.length === 0)
          ) {
            clearLocalDraft(orderId);
            setLocalDraftLines([]);
            setOrderOverride(latestOrder);
            setPaymentProgress({
              title: "Done",
              message: "Payment completed. Returning to the table page.",
              value: 100,
            });
            setSuccessMessage("Payment completed. Returning to table page...");
            setTimeout(() => {
              setShowPayModal(false);
              setError(null);
              setSuccessMessage(null);
              setIsRefreshingAfterPayment(false);
              cartLeaveGuard.allowNextNavigation();
              navigate("/pos", { replace: true });
            }, 1200);
            return;
          }

          if (isPaymentConfirmationRequiredError(submitErr)) {
            setSuccessMessage(null);
            setIsRefreshingAfterPayment(false);
            setError(
              "Payment confirmation is required before submitting to kitchen. Please complete the payment confirmation and try again.",
            );
            return;
          }
          setSuccessMessage(null);
          setIsRefreshingAfterPayment(false);
          setError(getPosErrorMessage(submitErr));
        }
      }
      // In PAY_LATER mode, return to seat board (order is complete)
      else {
        setSuccessMessage("Payment processed successfully.");
        setTimeout(() => setSuccessMessage(null), 3000);
        setTimeout(() => {
          setIsRefreshingAfterPayment(false);
          setShowPayModal(false);
          cartLeaveGuard.allowNextNavigation();
          navigate("/pos");
        }, 1200);
      }
    } catch (err) {
      if (isOrderingBlockedError(err)) {
        dispatch(api.util.invalidateTags(["StoreFeatures"]));
      }
      setSuccessMessage(null);
      setIsRefreshingAfterPayment(false);
      setError(getPosErrorMessage(err));
    } finally {
      setActivePaymentMethod(null);
    }
  };

  const handleChangeLineQty = async (line, delta) => {
    if (!line || !delta) return;
    setLocalDraftLines((currentLines) =>
      changeGroupedDraftLineQty({
        draftLines: currentLines,
        groupedLine: line,
        delta,
      }),
    );
  };

  const handleRemoveLine = async (line) => {
    if (!line) return;
    setLocalDraftLines((currentLines) =>
      removeGroupedDraftLine({
        draftLines: currentLines,
        groupedLine: line,
      }),
    );
  };

  const getNumeric = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const getLineDisplaySubtotal = (line) =>
    getNumeric(getLineSubtotalValue(line));

  const hasKitchenPendingQty = (line) =>
    getKitchenStatus(line, getNumeric).qtyPending > 0;

  const groupedDraftLines = React.useMemo(
    () =>
      groupDisplayLines(
        localDraftLines,
        getNumeric,
        getLineDisplaySubtotal,
      )
        .map((line) => ({
          ...line,
          kitchenStatus: getKitchenStatus(line, getNumeric),
        }))
        .sort((a, b) => {
          const statusRank = {
            NOT_SENT: 0,
            PARTIALLY_SENT: 1,
            SENT: 2,
          };
          const rankDiff =
            (statusRank[a.kitchenStatus.code] ?? 99) -
            (statusRank[b.kitchenStatus.code] ?? 99);
          if (rankDiff !== 0) return rankDiff;
          return String(a.itemName || "").localeCompare(
            String(b.itemName || ""),
          );
        }),
    [localDraftLines, getLineDisplaySubtotal],
  );

  const pendingDisplayLines = React.useMemo(
    () =>
      groupedDraftLines
        .filter((line) => line.kitchenStatus.qtyPending > 0)
        .map((line) => ({
          ...line,
          displayQty: line.kitchenStatus.qtyPending,
          displaySubtotal: getSplitLineSubtotal(
            line,
            line.kitchenStatus.qtyPending,
            getNumeric,
          ),
          displayTotal: getSplitLineTotal(
            line,
            line.kitchenStatus.qtyPending,
            getNumeric,
          ),
        })),
    [groupedDraftLines],
  );

  const groupedSentLines = React.useMemo(
    () =>
      groupDisplayLines(
        effectiveOrderLines.filter(
          (line) => Number(line?.qtySentToKitchen || 0) > 0 || line?.isSentToKitchen,
        ),
        getNumeric,
        getLineDisplaySubtotal,
      )
        .map((line) => ({
          ...line,
          kitchenStatus: getKitchenStatus(line, getNumeric),
        }))
        .sort((a, b) =>
          String(a.itemName || "").localeCompare(String(b.itemName || "")),
        ),
    [effectiveOrderLines, getLineDisplaySubtotal],
  );

  const sentDisplayLines = React.useMemo(
    () =>
      groupedSentLines
        .filter((line) => line.kitchenStatus.qtySent > 0)
        .map((line) => ({
          ...line,
          displayQty: line.kitchenStatus.qtySent,
          displaySubtotal: getSplitLineSubtotal(
            line,
            line.kitchenStatus.qtySent,
            getNumeric,
          ),
          displayTotal: getSplitLineTotal(
            line,
            line.kitchenStatus.qtySent,
            getNumeric,
          ),
        })),
    [groupedSentLines],
  );

  const isDraft = effectiveOrder?.status === "DRAFT";
  const canEdit = isDraft;
  const canEditNotes = !["COMPLETED", "CANCELLED"].includes(
    effectiveOrder?.status,
  );
  const canApplyDiscount = !["COMPLETED", "CANCELLED"].includes(
    effectiveOrder?.status,
  );
  const canAddItems = !["COMPLETED", "CANCELLED"].includes(
    effectiveOrder?.status,
  );

  const totals = effectiveOrder?.totals || {};
  const discounts = Array.isArray(effectiveOrder?.discounts)
    ? effectiveOrder.discounts
    : [];
  const serviceAmount = Number(
    totals.serviceCharge ?? totals.serviceFee ?? totals.serviceTotal ?? 0,
  );

  // Check if pending items need payment (for PAY_FIRST mode)
  const isPayFirstMode = posPaymentMode === "PAY_FIRST";
  const totalPaid = getOrderPaidAmount(effectiveOrder);
  const sentItemsSubTotal = sentDisplayLines.reduce(
    (sum, line) => sum + Number(line.displaySubtotal || 0),
    0,
  );
  const sentItemsTaxTotal = sentDisplayLines.reduce((sum, line) => {
    const subtotal = Number(line.displaySubtotal || 0);
    const total = Number(line.displayTotal || 0);

    if (total >= subtotal && total > 0) {
      return sum + (total - subtotal);
    }

    return sum + (isLineNonTaxable(line) ? 0 : subtotal * activeTaxRate);
  }, 0);
  const pendingItemsTotal = pendingDisplayLines.reduce(
    (sum, line) => sum + (line.displaySubtotal || 0),
    0,
  );
  const pendingItemsTaxTotal = pendingDisplayLines.reduce(
    (sum, line) =>
      sum +
      (isLineNonTaxable(line)
        ? 0
        : (line.displaySubtotal || 0) * activeTaxRate),
    0,
  );
  const subTotalAmount = truncateMoneyAmount(
    sentItemsSubTotal + pendingItemsTotal,
  );
  const taxAmount = truncateMoneyAmount(
    sentItemsTaxTotal + pendingItemsTaxTotal,
  );
  const grandTotalAmount = truncateMoneyAmount(subTotalAmount + taxAmount);
  const hasApiFooterTotals =
    effectiveOrder?.status !== "DRAFT" &&
    effectiveOrder?.totals &&
    Number.isFinite(Number(effectiveOrder?.totals?.grandTotal));
  const footerSubTotalAmount = truncateMoneyAmount(
    hasApiFooterTotals
      ? effectiveOrder?.totals?.subTotal
      : subTotalAmount,
  );
  const footerTaxAmount = truncateMoneyAmount(
    hasApiFooterTotals ? effectiveOrder?.totals?.tax : taxAmount,
  );
  const footerGrandTotalAmount = truncateMoneyAmount(
    hasApiFooterTotals
      ? effectiveOrder?.totals?.grandTotal
      : grandTotalAmount,
  );
  const unpaidAmount = Math.max(0, footerGrandTotalAmount - totalPaid);
  const hasCartItems =
    pendingDisplayLines.length > 0 || sentDisplayLines.length > 0;
  const hasSentItems = sentDisplayLines.length > 0;
  const cartLeaveGuard = useCartLeaveGuard({
    when:
      pendingDisplayLines.length > 0 &&
      !isSubmitting &&
      !showPostSubmitProcessing &&
      !isRefreshingAfterPayment,
  });
  const pendingItemsNeedPayment =
    isPayFirstMode &&
    pendingDisplayLines.length > 0 &&
    totalPaid < footerGrandTotalAmount;
  const isDineInOrder = String(location?.pathname || "").startsWith("/pos/orders/");
  const showHeaderCustomerDetails = isDineInOrder;
  let pendingCustomer = null;
  if (orderId) {
    try {
      const raw = sessionStorage.getItem(getPendingCustomerStorageKey(orderId));
      pendingCustomer = raw ? JSON.parse(raw) : null;
    } catch {
      pendingCustomer = null;
    }
  }
  const customerName = String(
    effectiveOrder?.customerName || pendingCustomer?.name || "",
  ).trim();
  const customerPhone = String(
    effectiveOrder?.customerPhone || pendingCustomer?.phone || "",
  ).trim();
  const customerEmail = String(
    effectiveOrder?.customerEmail || pendingCustomer?.email || "",
  ).trim();
  const hasCustomerDetails = Boolean(customerName || customerPhone || customerEmail);

  React.useEffect(() => {
    if (!orderId) return;
    if (
      !effectiveOrder?.customerName &&
      !effectiveOrder?.customerPhone &&
      !effectiveOrder?.customerEmail
    ) return;
    sessionStorage.removeItem(getPendingCustomerStorageKey(orderId));
  }, [
    orderId,
    effectiveOrder?.customerName,
    effectiveOrder?.customerPhone,
    effectiveOrder?.customerEmail,
  ]);

  // In PAY_FIRST mode, can only submit after pending items are paid for
  const canSubmit =
    canAddItems &&
    groupedDraftLines.some((line) => line.kitchenStatus.qtyPending > 0) &&
    (!isPayFirstMode || !pendingItemsNeedPayment);

  const compactFooterButtons = canSubmit;
  const footerActionButtonClass = compactFooterButtons
    ? "h-6 w-full px-1 text-[8px] leading-none whitespace-nowrap"
    : "h-7 w-full px-2 text-[10px] leading-none whitespace-nowrap";
  // In PAY_FIRST mode, show PAY button even for DRAFT orders.
  // In PAY_LATER mode, only show PAY when no pending (unsubmitted) items exist.
  const showPayButton = isPayFirstMode
    ? pendingItemsNeedPayment
    : !isDraft && pendingDisplayLines.length === 0 && unpaidAmount > 0;
  const canInitiatePayment = showPayButton && hasCartItems;
  const payButtonTitle = canInitiatePayment
    ? `Pay $${unpaidAmount.toFixed(2)}`
    : !hasCartItems
      ? "Add items before processing payment"
      : !isPayFirstMode && pendingDisplayLines.length > 0
        ? "Submit pending items before processing payment"
        : "No payment is currently due";

  const hasPendingCartItems = pendingDisplayLines.length > 0;
  const pendingItemQtyTotal = pendingDisplayLines.reduce(
    (sum, line) => sum + Math.max(0, Number(line?.qty || 0)),
    0,
  );

  const confirmCloseWithPendingItems = async () => {
    try {
      if (draftSyncTimeoutRef.current) {
        clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }
      clearLocalDraft(orderId);
      setLocalDraftLines([]);
      setClosePendingModalOpen(false);
      if (isDraft && totalPaid <= 0) {
        await closeOrder(orderId).unwrap();
        if (resolvedSeatId) {
          await releaseSeatLock();
        }
      } else if (!isDraft && resolvedSeatId) {
        await releaseSeatLock();
      }
      navigate("/pos");
    } catch (err) {
      setError(getPosErrorMessage(err));
    }
  };

  const footerButtonsCount =
    // 1 + // split - HIDDEN
    // 1 + // discount - HIDDEN
    1 + // close
    (canSubmit ? 1 : 0) + // submit
    1; // pay - always visible
  const submitButtonLabel = compactFooterButtons
    ? isSubmitting
      ? `SUBMIT... (${pendingItemQtyTotal})`
      : `SUBMIT (${pendingItemQtyTotal})`
    : isSubmitting
      ? `SUBMITTING... (${pendingItemQtyTotal})`
      : `SUBMIT TO KITCHEN (${pendingItemQtyTotal})`;

  // Helper text for PAY_FIRST mode
  const submitButtonHelper =
    isPayFirstMode && pendingItemsNeedPayment
      ? "Payment required before submitting to kitchen"
      : "";

  useKeyboardShortcuts(
    {
      [POS_SHORTCUTS.ESCAPE]: () => {
        if (showDiscount) setShowDiscount(false);
        else if (showNotesModal) setShowNotesModal(false);
        else if (showModifiers) setShowModifiers(false);
        else handleExitToSeatBoard();
      },
      [POS_SHORTCUTS.DISCOUNT]: () => {
        if (canEdit && !showDiscount) setShowDiscount(true);
      },
      [POS_SHORTCUTS.NOTES]: () => {
        if (canEditNotes && !showNotesModal) setShowNotesModal(true);
      },
      [POS_SHORTCUTS.SUBMIT]: () => {
        if (canSubmit) handleSubmit();
      },
      [POS_SHORTCUTS.PAYMENT]: () => {
        if (canInitiatePayment) handlePayment();
      },
    },
    !!effectiveOrder,
  );

  useEffect(() => {
    return () => {
      if (
        releaseAttemptedRef.current ||
        !resolvedSeatId
      ) {
        return;
      }
      releaseAttemptedRef.current = true;
      releaseSeat(resolvedSeatId)
        .unwrap()
        .catch((err) => {
          console.warn("[POS] Failed to release seat during unmount:", err);
        })
        .finally(() => {
          if (orderId) {
            sessionStorage.removeItem(`pos.orderSeatId.${orderId}`);
          }
        });
    };
  }, [resolvedSeatId, releaseSeat]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">
            Loading order...
          </p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Alert tone="error">Order not found</Alert>
      </div>
    );
  }

  const handleExitToSeatBoard = async () => {
    if (hasPendingCartItems) {
      setClosePendingModalOpen(true);
      return;
    }
    if (localDraftSignature !== serverDraftSignature) {
      try {
        await syncPendingDraftLines();
      } catch (err) {
        setError(getPosErrorMessage(err));
        return;
      }
    }

    if (isDraft && totalPaid <= 0) {
      try {
        await closeOrder(orderId).unwrap();
        if (resolvedSeatId) {
          await releaseSeatLock();
        }
      } catch (err) {
        setError(getPosErrorMessage(err));
        return;
      }
    }
    if (!isDraft && resolvedSeatId) {
      await releaseSeatLock();
    }

    navigate("/pos");
  };


  return (
    <div className="h-screen flex flex-col bg-slate-50 dark:bg-slate-950">
      {/* Real-time Update Notification */}
      {realtimeUpdate && (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
          <Alert tone="info">
            Order status updated: {realtimeUpdate.status}
          </Alert>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
          <Alert tone="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {/* Success Display */}
      {successMessage && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md">
          <Alert tone="success" onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        </div>
      )}

      {showPostSubmitProcessing && (
        <div className="fixed inset-0 z-[80] bg-slate-900/30">
          <div className="flex h-full items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
                <div>
                  <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    Processing order...
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    Please wait while we submit the order to the kitchen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleRequestClose}
              className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="flex items-center gap-2">
              <h1 className="text-base font-bold text-slate-900 dark:text-slate-100">
                {effectiveOrder?.seatRef}
              </h1>
              {orderDuration && (
                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-slate-700">
                  <svg
                    className="h-3 w-3 text-slate-600 dark:text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-300">
                    {orderDuration}
                  </span>
                </div>
              )}
              {showHeaderCustomerDetails && (
                <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
                  {hasCustomerDetails ? (
                    <div className="flex min-w-0 items-center gap-3 text-sm">
                      <div className="min-w-0 flex items-center gap-1.5">
                        <span className="truncate font-semibold text-slate-900 dark:text-slate-100">
                          {customerName || customerPhone || "-"}
                        </span>
                      </div>
                      <div className="h-4 w-px shrink-0 bg-slate-200 dark:bg-slate-700" />
                      <div className="min-w-0 flex items-center gap-1.5">
                        <span className="truncate text-slate-700 dark:text-slate-300">
                          {customerPhone || "-"}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      No customer added
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="inline-flex items-center whitespace-nowrap"
            onClick={cashSessionGuard.requestStartSession}
          >
            Start Session
          </Button>
        </div>
      </div>

      {/* Main Content - Split Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT SIDE - Order Details (40%) */}
        <div className="w-2/5 border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
          <div className="flex-1 flex flex-col overflow-hidden">
            <>
                <div className="flex-1 min-h-0 border-b border-slate-200 dark:border-slate-700">
                  <div className="flex h-full flex-col p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Items to Add
                      </h2>
                      <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                        Pending
                      </span>
                    </div>

                    {pendingDisplayLines.length > 0 ? (
                      <>
                        <div className="grid grid-cols-12 gap-1.5 px-2 pb-1 text-[10px] font-semibold text-orange-700 dark:text-orange-300">
                          <div className="col-span-4">Product</div>
                          <div className="col-span-2 text-right">Price</div>
                          <div className="col-span-2 pr-2 text-right">
                            Total
                          </div>
                          <div className="col-span-3 pl-2 text-center">
                            Qty
                          </div>
                          <div className="col-span-1 text-right"> </div>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pos-scrollbar pr-1">
                          <div className="space-y-1">
                          {pendingDisplayLines.map((line) => (
                            <div
                              key={`${line.orderLineId}-pending`}
                              className={`grid grid-cols-12 gap-1.5 items-center px-2 py-1 border-b border-orange-200 dark:border-orange-800 border-l-2 ${
                                line.kitchenStatus.code === "PARTIALLY_SENT"
                                  ? "border-l-amber-500 bg-amber-50/30 dark:bg-amber-900/10"
                                  : "border-l-orange-500 bg-orange-50/30 dark:bg-orange-900/10"
                              }`}
                            >
                              <div className="col-span-4">
                                <p className="text-[10px] font-medium leading-tight text-slate-900 dark:text-slate-100 truncate">
                                  {line.itemName}
                                </p>
                              {isLineNonTaxable(line) && (
                                  <p className="text-[9px] font-medium text-amber-700 dark:text-amber-300 truncate">
                                    Non Taxable
                                  </p>
                                )}
                              {line.modifiers && line.modifiers.length > 0 && (
                                  <p className="text-[9px] text-slate-500 dark:text-slate-400 truncate">
                                    {getModifierDisplayText(line.modifiers)}
                                  </p>
                                )}
                              </div>
                              <div className="col-span-2 text-right text-[10px] text-slate-600 dark:text-slate-400">
                                ${getEffectiveUnitPrice(line).toFixed(2)}
                              </div>
                              <div className="col-span-2 pr-1 text-right text-[10px] font-bold text-slate-900 dark:text-slate-100">
                                ${Number(line.displaySubtotal || 0).toFixed(2)}
                              </div>
                              <div className="col-span-3 pl-1 flex items-center justify-center gap-1.5">
                                {canAddItems && (
                                  <button
                                    type="button"
                                    className="h-7 w-8 rounded-md border border-slate-300 dark:border-slate-600 text-[11px] font-extrabold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition"
                                    onClick={() =>
                                      handleChangeLineQty(line, -1)
                                    }
                                    aria-label="Decrease quantity"
                                  >
                                    -
                                  </button>
                                )}
                                <span className="min-w-[8px] text-center text-[10px] font-semibold text-slate-900 dark:text-slate-100">
                                  {line.displayQty}
                                </span>
                                {canAddItems && (
                                  <button
                                    type="button"
                                    className="h-7 w-8 rounded-md border border-slate-300 dark:border-slate-600 text-[11px] font-extrabold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition"
                                    onClick={() => handleChangeLineQty(line, 1)}
                                    aria-label="Increase quantity"
                                  >
                                    +
                                  </button>
                                )}
                              </div>
                              <div className="col-span-1 text-right">
                                {canAddItems && (
                                  <button
                                    type="button"
                                    className="h-7 w-7 inline-flex items-center justify-center text-slate-400 hover:text-red-500 transition"
                                    onClick={() => handleRemoveLine(line)}
                                    aria-label="Remove item"
                                  >
                                    <svg
                                      className="h-3.5 w-3.5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 min-h-0 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
                        No pending items
                      </div>
                    )}
                  </div>
                </div>

                {!isPayFirstMode && (
                <div className="flex-1 min-h-0">
                  <div className="flex h-full flex-col p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                        Already Sent for {effectiveOrder?.seatRef}
                      </h2>
                      <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                        Sent
                      </span>
                    </div>

                    {sentDisplayLines.length > 0 ? (
                      <>
                        <div className="grid grid-cols-12 gap-1.5 px-2 pb-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
                          <div className="col-span-5">Product</div>
                          <div className="col-span-2 text-right">Price</div>
                          <div className="col-span-2 pr-2 text-right">
                            Total
                          </div>
                          <div className="col-span-2 pl-2 text-center">
                            Qty
                          </div>
                          <div className="col-span-1 text-right"> </div>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden pos-scrollbar pr-1">
                          <div className="space-y-1">
                          {sentDisplayLines.map((line) => (
                            <div
                              key={`${line.orderLineId}-sent`}
                              className="grid grid-cols-12 gap-1.5 items-center px-2 py-1 border-b border-emerald-200 dark:border-emerald-800 border-l-2 border-l-emerald-500 bg-emerald-50/30 dark:bg-emerald-900/10"
                            >
                              <div className="col-span-5">
                                <p className="text-[10px] font-medium leading-tight text-slate-900 dark:text-slate-100 truncate">
                                  {line.itemName}
                                </p>
                                {isLineNonTaxable(line) && (
                                  <p className="text-[9px] font-medium text-amber-700 dark:text-amber-300 truncate">
                                    Non Taxable
                                  </p>
                                )}
                                {line.modifiers && line.modifiers.length > 0 && (
                                  <p className="text-[9px] text-slate-500 dark:text-slate-400 truncate">
                                    {getModifierDisplayText(line.modifiers)}
                                  </p>
                                )}
                              </div>
                              <div className="col-span-2 text-right text-[10px] text-slate-600 dark:text-slate-400">
                                ${getEffectiveUnitPrice(line).toFixed(2)}
                              </div>
                              <div className="col-span-2 pr-1 text-right text-[10px] font-bold text-slate-900 dark:text-slate-100">
                                ${Number(line.displaySubtotal || 0).toFixed(2)}
                              </div>
                              <div className="col-span-2 pl-1 text-center text-[10px] font-semibold text-slate-900 dark:text-slate-100">
                                {line.displayQty}
                              </div>
                              <div className="col-span-1" />
                            </div>
                          ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 min-h-0 flex items-center justify-center text-xs text-slate-500 dark:text-slate-400">
                        No items on order
                      </div>
                    )}
                    </div>
                  </div>
                )}
              </>
          </div>

          {/* Left Panel Bottom Bar - Actions and Total */}
          <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-3">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-3 text-[10px] text-slate-600 dark:text-slate-400">
                <div className="min-w-0 flex flex-nowrap items-center gap-x-3 overflow-x-auto">
                  <span className="whitespace-nowrap">
                    Subtotal:{" "}
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      ${footerSubTotalAmount.toFixed(2)}
                    </span>
                  </span>
                  {/* Discount value hidden for now. */}
                  <span className="text-xs whitespace-nowrap">
                    Tax:{" "}
                    <span className="text-xs font-semibold text-slate-900 dark:text-slate-100">
                      ${footerTaxAmount.toFixed(2)}
                    </span>
                  </span>
                  {serviceAmount > 0 && (
                    <span className="whitespace-nowrap">
                      Service:{" "}
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        ${serviceAmount.toFixed(2)}
                      </span>
                    </span>
                  )}
                </div>
                <span className="shrink-0 text-xs font-semibold text-slate-700 dark:text-slate-300 whitespace-nowrap">
                  Total:{" "}
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                    ${footerGrandTotalAmount.toFixed(2)}
                  </span>
                </span>
              </div>

              <div
                className="grid w-full gap-1"
                style={{
                  gridTemplateColumns: `repeat(${footerButtonsCount}, minmax(0, 1fr))`,
                }}
              >
                {/* DISCOUNT BUTTON - TEMPORARILY HIDDEN */}
                {/* <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowDiscount(true)}
                  disabled={!canApplyDiscount}
                  className={footerActionButtonClass}
                >
                  DISCOUNT
                </Button> */}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleRequestClose}
                  disabled={isClosingOrder}
                  className={`${footerActionButtonClass} text-red-600 hover:text-red-700 dark:text-red-400`}
                >
                  CLOSE
                </Button>
                {/* <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setShowNotesModal(true)}
                  disabled={!canEditNotes}
                  className={footerActionButtonClass}
                >
                  NOTES
                </Button> */}
                <Button
                  size="sm"
                  onClick={handlePayment}
                  disabled={!canInitiatePayment}
                  className={`${footerActionButtonClass} bg-indigo-600 text-white hover:bg-indigo-700 border-indigo-600`}
                  title={payButtonTitle}
                >
                  PAY
                </Button>
                {/* Show SUBMIT button when items can be submitted */}
                {canSubmit && (
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className={footerActionButtonClass}
                    title={submitButtonHelper}
                  >
                    {submitButtonLabel}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - Kiosk Menu (60%) */}
        <div className="w-3/5 bg-slate-100 dark:bg-slate-800 flex overflow-hidden">
          {/* Items Area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <svg
                    className="h-4 w-4 text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 pl-9 pr-9 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2.5">
              {showParentCategoryBrowser ? (
                <div className="space-y-4">
                  {showChildCategorySection && (
                    <div className="space-y-2">
                      {useCategorySectionHeading && (
                        <h4 className="px-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Categories
                        </h4>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        {visibleChildCategories.map((category, index) => {
                          const colorVariants = [
                            "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
                            "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                            "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                            "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                            "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                            "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                          ];
                          const colorClass =
                            colorVariants[index % colorVariants.length];

                          return (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => setCategoryFilter(category.id)}
                              className={`min-h-[84px] rounded-lg border ${colorClass} p-2 flex flex-col items-center justify-center text-center hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all`}
                            >
                              <p className="text-xs font-medium uppercase tracking-wide text-slate-700 dark:text-slate-300 mb-1">
                                Category
                              </p>
                              <p className="text-sm font-black text-slate-900 dark:text-slate-100 line-clamp-2">
                                {category.name}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {showParentItemSection && (
                    <div className="space-y-2">
                      {useItemSectionHeading && (
                        <h4 className="px-1 text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Items
                        </h4>
                      )}
                      <div className="grid grid-cols-4 gap-2">
                        {selectedParentItems.map((item, index) => {
                          const colorVariants = [
                            "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
                            "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                            "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                            "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                            "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                            "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                          ];
                          const colorClass =
                            colorVariants[index % colorVariants.length];

                          return (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => handleItemSelect(item)}
                              className={`min-h-[84px] rounded-lg border ${colorClass} p-2 flex flex-col items-center justify-center text-center hover:shadow-md hover:scale-[1.01] active:scale-[0.99] transition-all`}
                            >
                              <p className="text-xs font-bold text-slate-900 dark:text-slate-100 line-clamp-2 mb-0.5">
                                {item.name}
                              </p>
                              <p className="text-sm font-black text-slate-900 dark:text-slate-100">
                                ${item.basePrice.toFixed(2)}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {!searchQuery && selectedCategory?.parentCategoryId && (
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() =>
                          setCategoryFilter(String(selectedCategory.parentCategoryId))
                        }
                        className="inline-flex items-center rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        Back
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-4 gap-2">
                    {filteredItems.map((item, index) => {
                      const isSoldOut = item?.isAvailable === false;
                      const colorVariants = [
                        "bg-amber-100 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700",
                        "bg-rose-100 dark:bg-rose-900/30 border-rose-300 dark:border-rose-700",
                        "bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700",
                        "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-300 dark:border-emerald-700",
                        "bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700",
                        "bg-cyan-100 dark:bg-cyan-900/30 border-cyan-300 dark:border-cyan-700",
                      ];
                      const colorClass =
                        colorVariants[index % colorVariants.length];

                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => handleItemSelect(item)}
                          disabled={isSoldOut}
                          className={`min-h-[84px] rounded-lg border ${
                            isSoldOut
                              ? "border-slate-300 bg-slate-100 text-slate-500 grayscale opacity-70 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
                              : `${colorClass} hover:shadow-md hover:scale-[1.01] active:scale-[0.99]`
                          } p-2 flex flex-col items-center justify-center text-center transition-all disabled:cursor-not-allowed`}
                        >
                          {isSoldOut && (
                            <span className="mb-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-black uppercase text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                              Sold out
                            </span>
                          )}
                          <p className={`text-xs font-bold line-clamp-2 mb-0.5 ${
                            isSoldOut
                              ? "text-slate-500 dark:text-slate-400"
                              : "text-slate-900 dark:text-slate-100"
                          }`}>
                            {item.name}
                          </p>
                          <p className={`text-sm font-black ${
                            isSoldOut
                              ? "text-slate-500 dark:text-slate-400"
                              : "text-slate-900 dark:text-slate-100"
                          }`}>
                            ${item.basePrice.toFixed(2)}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {!showParentCategoryBrowser &&
                isMenuLoading &&
                filteredItems.length === 0 && (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border-2 border-slate-300 border-t-indigo-500 animate-spin" />
                      <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-400">
                        Loading items...
                      </p>
                    </div>
                  </div>
                )}

              {!showParentCategoryBrowser &&
                !isMenuLoading &&
                filteredItems.length === 0 && (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <svg
                      className="mx-auto h-10 w-10 text-slate-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                      {searchQuery
                        ? "No items match your search"
                        : "No items available right now"}
                    </p>
                    {!searchQuery && selectedCategory?.parentCategoryId && (
                      <button
                        type="button"
                        onClick={() =>
                          setCategoryFilter(String(selectedCategory.parentCategoryId))
                        }
                        className="mt-3 inline-flex items-center rounded-lg bg-slate-200 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
                      >
                        Back
                      </button>
                    )}
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Category Rail */}
          <div className="w-44 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-col">
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700">
              <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Categories
              </h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              <button
                type="button"
                onClick={() => setCategoryFilter("")}
                className={`w-full min-h-[48px] text-left px-4 py-3 rounded-lg text-sm font-bold uppercase transition ${
                  !categoryFilter
                    ? "bg-indigo-600 text-white shadow"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                }`}
              >
                All Items
              </button>
              {visibleParentCategories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setCategoryFilter(category.id)}
                    className={`w-full min-h-[48px] text-left px-4 py-3 rounded-lg text-sm font-semibold transition ${
                      activeRailCategoryId === category.id
                        ? "bg-indigo-600 text-white shadow"
                        : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Modifier Selector */}
      {selectedItem && (
        <ModifierSelector
          isOpen={showModifiers}
          onClose={() => {
            setShowModifiers(false);
            setSelectedItem(null);
            setModifierPreset([]);
            setSkipUpsellForSelectedItem(false);
          }}
          onConfirm={handleAddItem}
          modifierGroups={selectedItemModifierGroups}
          selectedModifiers={modifierPreset}
          itemName={selectedItem.name}
          itemPrice={selectedItem.basePrice}
        />
      )}

      {/* Discount Form */}
      <DiscountForm
        isOpen={showDiscount}
        onClose={() => setShowDiscount(false)}
        orderId={orderId}
        orderSubtotal={effectiveOrder?.totals?.subTotal}
        discounts={discounts}
      />

      {/* Notes Modal */}
      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Order Notes"
        confirmText="Save"
        onConfirm={handleUpdateNotes}
        confirmDisabled={isUpdatingNotes}
      >
        <Textarea
          label="Notes"
          placeholder="Add special instructions or notes..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />
      </Modal>

      {/* Pay Modal */}
      <ProcessPaymentModal
        isOpen={showPayModal}
        onClose={() => setShowPayModal(false)}
        subtotal={footerSubTotalAmount}
        tax={footerTaxAmount}
        total={footerGrandTotalAmount}
        amountDue={Math.max(unpaidAmount, 0)}
        paymentError={paymentError}
        tipConfig={tipConfig}
        isProcessing={isProcessingPayment || isRefreshingAfterPayment || isPreparingPayment}
        activePaymentMethod={activePaymentMethod}
        onCash={(amount, tipAmount) =>
          handleProcessPayment("CASH", amount, tipAmount)
        }
        onCard={(amount, tipAmount) =>
          handleProcessPayment("CARD", amount, tipAmount)
        }
      />

      <CashSessionStartupModal
        autoPrompt={false}
        isOpenRequested={cashSessionGuard.isStartSessionRequested}
        onSessionStarted={cashSessionGuard.clearStartSessionRequest}
      />

      {/* Confirm Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null })}
        title={
          confirmModal.action === "CLOSE_ORDER"
            ? "Close Order"
            : "Submit To Kitchen"
        }
        message={
          confirmModal.action === "CLOSE_ORDER"
            ? "You have unsent draft items. Closing will discard them. Are you sure you want to close this order?"
            : "Submit this order to the kitchen?"
        }
        confirmText={confirmModal.action === "CLOSE_ORDER" ? "Close" : "Submit"}
        confirmVariant="pos-primary"
        onConfirm={handleConfirmAction}
        isLoading={isSubmitting}
      />
      <Modal
        isOpen={closePendingModalOpen}
        onClose={() => setClosePendingModalOpen(false)}
        title="Close Table?"
        message="Close this table? Unsaved cart items will be removed."
        confirmText="Yes, Close Table"
        cancelText="Keep Editing"
        confirmVariant="danger"
        onConfirm={confirmCloseWithPendingItems}
      />
      <Modal
        isOpen={cartLeaveGuard.isOpen}
        onClose={cartLeaveGuard.cancelLeave}
        title="Leave Page?"
        message="You have unsent cart items. Leaving this page will remove them. Are you sure you want to continue?"
        confirmText="Leave Page"
        cancelText="Keep Editing"
        confirmVariant="danger"
        onConfirm={cartLeaveGuard.confirmLeave}
      />
      <Modal
        isOpen={stationRequiredModalOpen}
        onClose={() => setStationRequiredModalOpen(false)}
        title="Station Assignment Required"
        message={ITEM_STATION_REQUIRED_MESSAGE}
        onConfirm={() => setStationRequiredModalOpen(false)}
        confirmText="OK"
        showCancel={false}
      />
      {(isPreparingPayment || isRefreshingAfterPayment) && (
        <div className="fixed inset-0 z-[80] bg-slate-900/30">
          <div className="flex h-full items-center justify-center px-4">
            <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-900">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600 dark:border-slate-700 dark:border-t-indigo-400" />
                <div>
                  <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                    {paymentProgress.title}
                  </p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {paymentProgress.message}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Upsell Modal */}
      <UpsellSuggestionModal
        isOpen={upsellModal.isOpen}
        onClose={() =>
          setUpsellModal({
            isOpen: false,
            triggerItemName: "",
            triggerItemPrice: null,
            suggestions: [],
          })
        }
        triggerItemName={upsellModal.triggerItemName}
        triggerItemPrice={upsellModal.triggerItemPrice}
        suggestions={upsellModal.suggestions}
        onConfirm={handleConfirmUpsellItems}
        isAdding={false}
      />

    </div>
  );
};

export default OrderEditor;




